/**
 * Revenue Presentation Model
 *
 * READ-ONLY data model — no backend formulas are recomputed here.
 * All values are read from amount_override / amount_calculated as written
 * by the accounting worker. Client-side filtering is required because
 * AccountingFilter does not expose a 'revenue' kind variant.
 */

import { AccountingType } from 'v2-types/order';
import { formatMoney } from 'utils/helpers';
import {
  DebtEquityContext,
  GroupedMetrics,
  RevenueCode,
  RevenueCodeMeta,
  RevenueEntry,
  RevenueEntryAvailable,
  RevenueEntryUnavailable,
  RevenueGroup,
  RevenueKPIs,
} from './types';

// ─── 23-code registry ─────────────────────────────────────────────────────────

const REVENUE_CODE_META: ReadonlyArray<RevenueCodeMeta> = [
  // ── Base (#1-6) ──────────────────────────────────────────────────────────
  {
    code: 'revenue_invoiced',
    label: 'Revenue Invoiced',
    group: 'base',
    isRatio: false,
    isCurrency: true,
    isCashConversion: false,
  },
  {
    code: 'revenue_collected',
    label: 'Revenue Collected',
    group: 'base',
    isRatio: false,
    isCurrency: true,
    isCashConversion: false,
  },
  {
    code: 'revenue_pending',
    label: 'Revenue Pending',
    group: 'base',
    isRatio: false,
    isCurrency: true,
    isCashConversion: false,
  },
  {
    code: 'costs_incurred',
    label: 'Costs Incurred',
    group: 'base',
    isRatio: false,
    isCurrency: true,
    isCashConversion: false,
  },
  {
    code: 'costs_settled',
    label: 'Costs Settled',
    group: 'base',
    isRatio: false,
    isCurrency: true,
    isCashConversion: false,
  },
  {
    code: 'costs_open',
    label: 'Costs Open',
    group: 'base',
    isRatio: false,
    isCurrency: true,
    isCashConversion: false,
  },

  // ── Derived (#7-12) ──────────────────────────────────────────────────────
  {
    code: 'profit_net',
    label: 'Net Profit',
    group: 'derived',
    isRatio: false,
    isCurrency: true,
    isCashConversion: false,
  },
  {
    code: 'margin_gross',
    label: 'Gross Margin',
    group: 'derived',
    framing: 'Gross margin on total invoiced revenue',
    isRatio: true,
    isCurrency: false,
    isCashConversion: false,
  },
  {
    code: 'rate_collection',
    label: 'Collection Rate',
    group: 'derived',
    isRatio: true,
    isCurrency: false,
    isCashConversion: false,
  },
  {
    code: 'ratio_expense',
    label: 'Expense Ratio',
    group: 'derived',
    isRatio: true,
    isCurrency: false,
    isCashConversion: false,
  },
  {
    code: 'return_investment',
    label: 'ROI',
    group: 'derived',
    isRatio: true,
    isCurrency: false,
    isCashConversion: false,
  },
  {
    code: 'capital_working',
    label: 'Working Capital',
    group: 'derived',
    isRatio: false,
    isCurrency: true,
    isCashConversion: false,
  },

  // ── Executive (#13-23) ────────────────────────────────────────────────────
  {
    code: 'yield_markup',
    label: 'Markup Yield',
    group: 'executive',
    framing: 'Markup on sell-side / investor yield',
    isRatio: true,
    isCurrency: false,
    isCashConversion: false,
  },
  {
    code: 'income_net',
    label: 'Net Income',
    group: 'executive',
    isRatio: false,
    isCurrency: true,
    isCashConversion: false,
  },
  {
    code: 'margin_operating',
    label: 'Operating Margin',
    group: 'executive',
    isRatio: true,
    isCurrency: false,
    isCashConversion: false,
  },
  {
    code: 'assets_current',
    label: 'Current Assets',
    group: 'executive',
    isRatio: false,
    isCurrency: true,
    isCashConversion: false,
  },
  {
    code: 'liabilities_current',
    label: 'Current Liabilities',
    group: 'executive',
    isRatio: false,
    isCurrency: true,
    isCashConversion: false,
  },
  {
    code: 'ratio_current',
    label: 'Current Ratio',
    group: 'executive',
    isRatio: true,
    isCurrency: false,
    isCashConversion: false,
  },
  {
    code: 'debt_equity',
    label: 'Debt to Equity',
    group: 'executive',
    isRatio: true,
    isCurrency: false,
    isCashConversion: false,
  },
  {
    code: 'turnover_assets',
    label: 'Asset Turnover',
    group: 'executive',
    isRatio: true,
    isCurrency: false,
    isCashConversion: false,
  },
  {
    code: 'cashflow_free',
    label: 'Free Cash Flow',
    group: 'executive',
    isRatio: false,
    isCurrency: true,
    isCashConversion: false,
  },
  {
    code: 'conversion_cash',
    label: 'Cash Conversion Ratio',
    group: 'executive',
    isRatio: true,
    isCurrency: false,
    isCashConversion: true,
  },
  {
    code: 'score_efficiency',
    label: 'Efficiency Score',
    group: 'executive',
    isRatio: true,
    isCurrency: false,
    isCashConversion: false,
  },
];

/** Ordered set of all 23 codes for O(1) lookup */
const CODE_META_MAP: ReadonlyMap<RevenueCode, RevenueCodeMeta> = new Map(
  REVENUE_CODE_META.map((m) => [m.code, m])
);

export const REVENUE_CODES_BASE: ReadonlyArray<RevenueCode> = REVENUE_CODE_META
  .filter((m) => m.group === 'base')
  .map((m) => m.code);

export const REVENUE_CODES_DERIVED: ReadonlyArray<RevenueCode> = REVENUE_CODE_META
  .filter((m) => m.group === 'derived')
  .map((m) => m.code);

export const REVENUE_CODES_EXECUTIVE: ReadonlyArray<RevenueCode> = REVENUE_CODE_META
  .filter((m) => m.group === 'executive')
  .map((m) => m.code);

// ─── Amount resolver ──────────────────────────────────────────────────────────

/**
 * Resolves display amount per task spec:
 *   amount_override !== 0 && amount_override != null → use override
 *   otherwise                                         → use amount_calculated
 *   both null/undefined                               → null (unavailable)
 *
 * Never touches the `amount` field — it is not the computed revenue value.
 */
export const getRevenueDisplayAmount = (entry: AccountingType): number | null => {
  const override = entry.amount_override;
  const calculated = entry.amount_calculated;

  if (override != null && override !== 0) {
    return override;
  }

  if (calculated != null) {
    return calculated;
  }

  return null;
};

// ─── Display formatter ────────────────────────────────────────────────────────

const NO_BASIS_NOTE = 'No basis — denominator is zero';

/**
 * Formats a resolved amount for display according to the code metadata:
 *   - isCashConversion  → multiply by 100, show as percentage string
 *   - isRatio           → multiply by 100, show as percentage string
 *   - isCurrency        → formatMoney()
 *   - otherwise         → raw numeric string
 */
const formatRevenueAmount = (amount: number, meta: RevenueCodeMeta): string => {
  if (meta.isCashConversion || meta.isRatio) {
    return `${(amount * 100).toFixed(2)}%`;
  }
  if (meta.isCurrency) {
    return formatMoney(amount);
  }
  return String(amount);
};

// ─── Unavailable placeholder ──────────────────────────────────────────────────

const makeUnavailable = (meta: RevenueCodeMeta): RevenueEntryUnavailable => ({
  available: false,
  code: meta.code,
  label: meta.label,
  group: meta.group,
  framing: meta.framing,
  amount: null,
  display: '—',
  hasBasis: false,
  basisNote: NO_BASIS_NOTE,
  raw: null,
});

// ─── Entry builder ────────────────────────────────────────────────────────────

const buildEntry = (raw: AccountingType, meta: RevenueCodeMeta): RevenueEntry => {
  const amount = getRevenueDisplayAmount(raw);

  if (amount == null) {
    return makeUnavailable(meta);
  }

  // Zero-denominator guard: ratio metrics with amount === 0 may indicate
  // the worker wrote 0 because the denominator was zero (not a true 0%).
  // We expose hasBasis=false to let the consumer distinguish "no basis" from "actual zero".
  // NOTE: a true 0% result also arrives as 0; consumer should check basisNote in context.
  const hasBasis = !(meta.isRatio && amount === 0);
  const basisNote = hasBasis ? '' : NO_BASIS_NOTE;

  const entry: RevenueEntryAvailable = {
    available: true,
    code: meta.code,
    label: meta.label,
    group: meta.group,
    framing: meta.framing,
    amount,
    display: formatRevenueAmount(amount, meta),
    hasBasis,
    basisNote,
    raw,
  };

  return entry;
};

// ─── Kind filter ──────────────────────────────────────────────────────────────

/**
 * Defensive client-side filter: keep only entries whose kind is exactly "revenue".
 * Applied even when the GraphQL query has already been filtered, because
 * AccountingFilter does not formally expose a 'revenue' kind variant and
 * backend behaviour may change.
 */
export const filterRevenueEntries = (entries: AccountingType[]): AccountingType[] =>
  entries.filter((e) => /^revenue$/.test(e.kind));

// ─── Entry lookup ─────────────────────────────────────────────────────────────

/**
 * Finds a raw AccountingType entry matching a RevenueCode from the filtered set.
 */
const findRawEntry = (
  revenueEntries: AccountingType[],
  code: RevenueCode
): AccountingType | undefined =>
  revenueEntries.find((e) => e.code === code);

// ─── Grouping ─────────────────────────────────────────────────────────────────

/**
 * Maps all 23 known revenue codes against the filtered payload.
 * Missing codes are represented as unavailable placeholders (no crash).
 */
export const groupRevenueEntries = (revenueEntries: AccountingType[]): GroupedMetrics => {
  const resolve = (code: RevenueCode): RevenueEntry => {
    const meta = CODE_META_MAP.get(code);
    if (!meta) {
      // Should never happen — code is typed — but guard defensively
      const fallbackMeta: RevenueCodeMeta = {
        code,
        label: code,
        group: 'base' as RevenueGroup,
        isRatio: false,
        isCurrency: false,
        isCashConversion: false,
      };
      return makeUnavailable(fallbackMeta);
    }
    const raw = findRawEntry(revenueEntries, code);
    return raw != null ? buildEntry(raw, meta) : makeUnavailable(meta);
  };

  return {
    base: REVENUE_CODES_BASE.map(resolve),
    derived: REVENUE_CODES_DERIVED.map(resolve),
    executive: REVENUE_CODES_EXECUTIVE.map(resolve),
  };
};

// ─── Debt-equity context ──────────────────────────────────────────────────────

export const resolveDebtEquityContext = (grouped: GroupedMetrics): DebtEquityContext => {
  const entry = grouped.executive.find((e) => e.code === 'debt_equity');
  const isNegative = entry?.available === true && entry.amount < 0;

  return {
    isNegativeEquity: isNegative,
    equityContext: isNegative
      ? 'Negative equity position — liabilities exceed assets'
      : '',
  };
};

// ─── KPI surface ──────────────────────────────────────────────────────────────

/**
 * Extracts the four highlighted KPI entries for top-level consumer display.
 *
 * KPI code mapping (backend contract codes):
 *   netProfit      → profit_net
 *   roi            → return_investment
 *   collectionRate → rate_collection
 *   freeCashFlow   → cashflow_free
 */
export const buildKPIs = (grouped: GroupedMetrics): RevenueKPIs => {
  const findIn = (group: RevenueEntry[], code: RevenueCode): RevenueEntry => {
    const found = group.find((e) => e.code === code);
    if (!found) {
      const meta = CODE_META_MAP.get(code);
      return makeUnavailable(meta ?? {
        code,
        label: code,
        group: 'derived' as RevenueGroup,
        isRatio: false,
        isCurrency: false,
        isCashConversion: false,
      });
    }
    return found;
  };

  return {
    netProfit: findIn(grouped.derived, 'profit_net'),
    roi: findIn(grouped.derived, 'return_investment'),
    collectionRate: findIn(grouped.derived, 'rate_collection'),
    freeCashFlow: findIn(grouped.executive, 'cashflow_free'),
  };
};
